import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, 
  TrendingUp, 
  TrendingDown, 
  Briefcase, 
  Zap,
  Loader2,
  User,
  Crown,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicPortfolio = () => {
  const { shareCode } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicPortfolio = async () => {
      try {
        const res = await fetch(`${API_URL}/api/creator/public/${shareCode}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Failed to load portfolio');
        }
        const portfolioData = await res.json();
        setData(portfolioData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicPortfolio();
  }, [shareCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800 max-w-md">
          <CardContent className="p-8 text-center">
            <div className="text-red-400 text-lg mb-2">Portfolio Not Found</div>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gainPct = data.portfolio_value 
    ? ((data.portfolio_value - 100000) / 100000 * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="public-portfolio">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-white">
              {data.custom_title || `${data.username}'s Portfolio`}
            </h1>
            {data.is_pro && (
              <Badge className="bg-yellow-500/20 text-yellow-400">
                <Crown className="h-3 w-3 mr-1" />
                Pro
              </Badge>
            )}
          </div>
          <p className="text-slate-400">{data.season}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4 text-center">
              <Trophy className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">#{data.rank || '?'}</p>
              <p className="text-slate-400 text-sm">of {data.total_players} players</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4 text-center">
              <Briefcase className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                V${(data.portfolio_value || 100000).toLocaleString()}
              </p>
              <p className="text-slate-400 text-sm">Portfolio Value</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4 text-center">
              {gainPct >= 0 ? (
                <TrendingUp className="h-6 w-6 text-emerald-400 mx-auto mb-2" />
              ) : (
                <TrendingDown className="h-6 w-6 text-red-400 mx-auto mb-2" />
              )}
              <p className={`text-2xl font-bold ${gainPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {gainPct >= 0 ? '+' : ''}{gainPct.toFixed(2)}%
              </p>
              <p className="text-slate-400 text-sm">Total Return</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4 text-center">
              <BarChart3 className="h-6 w-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{data.total_points?.toLocaleString() || 0}</p>
              <p className="text-slate-400 text-sm">Season Points</p>
            </CardContent>
          </Card>
        </div>

        {/* Holdings */}
        {data.holdings && data.holdings.length > 0 && (
          <Card className="bg-slate-900 border-slate-800 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-400" />
                Holdings ({data.holdings_count}/10)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.holdings.map((holding) => (
                  <div 
                    key={holding.symbol}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      holding.is_power_play 
                        ? 'bg-amber-500/10 border border-amber-500/30' 
                        : 'bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {holding.symbol.slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{holding.symbol}</span>
                          <Badge variant="outline" className="text-xs border-slate-600">
                            {holding.type}
                          </Badge>
                          {holding.is_power_play && (
                            <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                              <Zap className="h-3 w-3 mr-1" />
                              3x
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-white">V${holding.value?.toLocaleString()}</p>
                      <p className="text-slate-400 text-sm">{holding.allocation_pct}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly Performance Chart */}
        {data.weekly_scores && data.weekly_scores.length > 0 && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-400" />
                Weekly Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {data.weekly_scores.map((score) => (
                  <div 
                    key={score.week}
                    className="flex-shrink-0 w-16 text-center p-2 bg-slate-800/50 rounded-lg"
                  >
                    <p className="text-xs text-slate-400 mb-1">Wk {score.week}</p>
                    <p className={`font-bold ${score.total_points >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {score.total_points}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          <p>Powered by Trader Titans</p>
        </div>
      </div>
    </div>
  );
};

export default PublicPortfolio;
